import React from 'react'
import { Route, Routes } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import ProposalsView from 'features/proposals/ProposalsView'
import { useOrgGuardStore } from '../OrgGuardStore'
import { PlatformContainer } from 'reusable/containers/PlatformContainer'
import { useAuthStore } from 'auth/AuthStore'
import Button from '@mui/material/Button'

import AnnouncementStore, { AnnouncementStoreProvider } from '../main-container/announcement/AnnouncementStore'
import useStore from 'reusable/mobx-store-utils'
import { useWebSocketStore } from 'websockets/WebSocketStore'
import PrintStore, { PrintStoreProvider } from 'reusable/printing/PrintStore'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import { DocViewViewMyResponses } from 'features/docs/view/DocView'
import DocViewPlanPage from 'features/docs/plan/DocViewPlanPage'
import { lazyWithRetry } from 'reusable/new-version/lazyWithRetry'
// Lazy load heavy components to avoid conflicts with DocRouter lazy loading
const ProposalsViewAccepted = lazyWithRetry(() => import('features/proposals/view/ProposalsViewAccepted'))
const DocEditView = lazyWithRetry(() => import('features/docs/client-edit/DocClientEditView'))
const MarkdownDeliverableView = lazyWithRetry(() => import('features/meetings/deliverable-view/MarkdownDeliverableView'))

export default observer(function GuestRouter() {
   const orgGuardStore = useOrgGuardStore()
   const webSocketStore = useWebSocketStore()
   const announcementStore = useStore(sp => new AnnouncementStore(sp), { webSocketStore, orgGuardStore, userInfo: orgGuardStore.userData })
   const printStore = useStore((sp) => new PrintStore(sp), { authStore: orgGuardStore.sp.authStore })

   if (orgGuardStore.loading) {
      return <PlatformContainer loadingMessage="Loading..." loading={true} contentCentered={true} />
   }

   return <PrintStoreProvider store={printStore}>
      <AnnouncementStoreProvider store={announcementStore}>
         <React.Suspense fallback={<PlatformContainer loadingMessage="Loading..." loading={true} contentCentered={true} />}>
            <Routes>
               {/* Should a user land on the homepage, give them the option to log out as guest */}
               <Route path="/" element={<GuestRouterHomePage />} />

               {/* Route for looking at a proposal */}
               <Route path="proposals/:proposalId" element={<ProposalsView showInPlatformContainer />} />

               {/* Route to view an accepted proposal */}
               <Route path="documents/proposal/:proposalId" element={<ProposalsViewAccepted showInPlatformContainer />} />

               {/* Route to edit view of user's document (doing pre-work / feedback) */}
               <Route path="documents/edit/:docId/*" element={<DocEditView />} />

               {/* Route for viewing a document */}
               <Route path="documents/:docId" element={<DocViewViewMyResponses />} />

               <Route path="documents/plans/business-plan" element={<DocViewPlanPage finalised={true} />} />

               {/* Route for viewing a shared meeting deliverable */}
               <Route path="documents/deliverable/:deliverableType/:meetingId" element={<MarkdownDeliverableView />} />
            </Routes>
         </React.Suspense>
      </AnnouncementStoreProvider>
   </PrintStoreProvider>
})

const GuestRouterHomePage = observer(function GuestRouterHomePage() {
   const authStore = useAuthStore()
   const regionRouterStore = useRegionRouterStore()

   const guestRouterStore = useStore(sp => ({
      signOut: async () => {
         await sp.authStore.signOut()
         sp.regionRouterStore.navigate('/')
      }
   }), { authStore, regionRouterStore })

   return <PlatformContainer loading={false} contentCentered={true}>
      <Button variant="text" onClick={guestRouterStore.signOut}>
         Return home
      </Button>
   </PlatformContainer>
})
